import React from 'react';
import { Option } from 'types/types';
import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import WizardTextInputComponent from 'components/v2/epd-wizard-inputs/WizardTextInput';
import { useUpdateToolMutation } from 'services/api/adminToolsQueriesAndMutations';
import { FieldPanel, FieldPanelHeader } from 'styles/v2/Styles.styled';
import getToolTypeLabel from 'util/getToolTypeLabel';
import { ToolVersionState } from 'types/tools/ToolVersionState';
import { PatchedToolForAdminModel } from 'types/admin-tools/PatchedToolForAdminModel';
import { FormikLikeErrors } from './utils/getErrorsLikeFormik';
import { IsReadOnlyModeContext } from 'components/v2/compiler/hooks/useIsReadOnlyMode';
import { ToolType } from 'types/tools/ToolType';
import AdminEditToolHeadCompanySelector from './AdminEditToolHeadCompanySelector';
import { confirmInfo } from 'components/v2/confirm-dialog/confirmInfo';
import { exactMatchesFirstCustomOptionsFilter } from 'components/form/SelectWithCustomOptionsFilter';
import styled from 'styled-components';

interface Props {
  toolData: PatchedToolForAdminModel;
  errors: FormikLikeErrors<PatchedToolForAdminModel>;
  countries: Option[];
}

const booleanYesNoOptions = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

const AdminEditToolHead: React.FC<Props> = ({ toolData, errors, countries }) => {
  const updateTool = useUpdateToolMutation(toolData.id);

  const handlePropertyChanged = (propertyName: string, value: any) => {
    updateTool.mutate({ propertyName, value, versionOrd: null });
  };
  const isReadOnlyMode = toolData.versions
    .filter((v) => v !== 'skeleton')
    .some((v) => v.versionState === ToolVersionState.ENABLED);
  const isDraftMode = toolData.versions
    .filter((v) => v !== 'skeleton')
    .every((v) => v.versionState === ToolVersionState.DRAFT);
  const handleApiIntegrationChanged = (name: string, value: boolean) => {
    if (!isDraftMode && value && !toolData.hasApiIntegration) {
      confirmInfo({
        caption: 'Restricted',
        description: `It is not allowed to update the integration status from "No" to "Yes" on an existing registration.`,
      });
      return;
    }
    handlePropertyChanged(name, value);
  };

  const handleOptionChanged = (options: Option[]) => (propertyName: string, value: any) => {
    const newOption = options.find((o) => o.value === value);
    handlePropertyChanged(propertyName, newOption);
  };

  return (
    <IsReadOnlyModeContext.Provider value={isReadOnlyMode}>
      <FieldPanel style={{ marginTop: 60, alignItems: 'flex-start' }}>
        <FieldPanelHeader className="w-full">General tool information</FieldPanelHeader>

        <WizardTextInputComponent
          label="Tool name"
          placeholder="Type here"
          name="name"
          error={errors.name}
          value={toolData.name}
          maxLength={100}
          onChanged={handlePropertyChanged}
          containerClassName="w-full"
        />

        <WizardTextInputComponent label="Tool type" name="toolType" value={getToolTypeLabel(toolData.toolType)} disabled />

        <CompilerSelectControlledComponent
          label="API integration"
          tooltip="Only Fully Pre-verified tools can have an API integration at this time."
          name="hasApiIntegration"
          error={errors.hasApiIntegration}
          options={booleanYesNoOptions}
          value={booleanYesNoOptions.find((v) => v.value === toolData.hasApiIntegration)}
          onChanged={handleApiIntegrationChanged}
          placeholder="Select"
          isClearable={false}
        />

        {toolData.toolType === ToolType.FULLY_PREVERIFIED_TOOL && (
          <AdminEditToolHeadCompanySelector
            name="fullyPreverifiedCompany"
            label="Assigning the Tool to an Approved Company"
            value={toolData.fullyPreverifiedCompany}
            onChanged={handlePropertyChanged}
            error={errors.fullyPreverifiedCompany}
            addButtonText="+ Add Company"
          />
        )}

        <AdminEditToolHeadCompanySelector
          name="toolOwnerCompany"
          label="Tool owner"
          value={toolData.toolOwnerCompany}
          onChanged={handlePropertyChanged}
          error={errors.toolOwnerCompany}
          addButtonText="+ Add Tool Owner Company"
        />

        <CompilerSelectControlledComponent
          label="Tool owner country"
          name="toolOwnerCountry"
          error={errors.toolOwnerCountry}
          options={countries}
          value={toolData.toolOwnerCountry}
          onChanged={handleOptionChanged(countries)}
          placeholder="Select"
          customOptionsFilter={exactMatchesFirstCustomOptionsFilter}
          isClearable
        />

        <WizardTextInputComponent
          label="Contact Person for the Tool"
          placeholder="Name and last name"
          name="contactPersonName"
          error={errors.contactPersonName}
          value={toolData.contactPersonName}
          maxLength={80}
          onChanged={handlePropertyChanged}
        />

        <WizardTextInputComponent
          label="Contact Person Email"
          placeholder="Type here"
          name="contactPersonEmail"
          error={errors.contactPersonEmail}
          value={toolData.contactPersonEmail}
          onChanged={handlePropertyChanged}
        />

        <WizardTextInputComponent
          label="Website"
          placeholder="Type here"
          name="website"
          error={errors.website}
          value={toolData.website}
          onChanged={handlePropertyChanged}
        />

        <WizardTextInputComponent
          label="Tool Description"
          tooltip="Add a short summary of what the tool does, its purpose, or any relevant details that help users understand its use."
          placeholder="Type here"
          name="description"
          error={errors.description}
          value={toolData.description}
          maxLength={500}
          onChanged={handlePropertyChanged}
          multiline
        />
        <HideOnPortalLabel>
          <input
            type="checkbox"
            checked={toolData.hideOnPortal ?? false}
            onChange={(e) => handlePropertyChanged('hideOnPortal', e.target.checked)}
          />
          Hide tool on EPD Portal (for testing purposes only, managed by the Development team)
        </HideOnPortalLabel>
      </FieldPanel>
    </IsReadOnlyModeContext.Provider>
  );
};

export default AdminEditToolHead;

const HideOnPortalLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  background: ${(props) => props.theme.colors.statusLabelRedBg};
  padding: 10px 12px;
  border-radius: 4px;
  width: 100%;
  grid-column: span 2;
`;
